// docs/17 §3 — the World lobby: who is here, their heroes, ready or not (the keeper may show someone
// the door), invitations, each player's heroes for this World, the World chat, and the World's
// options — live controls for the keeper, the same controls read-only for everyone else. Any option
// change un-readies every seat (server rule), so nobody is committed to a World they have not seen.
// 2026-10-23 mockup pass: the party-lobby frame shared with Lobby.tsx — top bar with the join policy,
// seat cards, YOUR HEROES tiles (the forge opens in a modal) beside the options, the chat column,
// and the bottom bar with the ready squares and the one gold action (Ready / Begin the World).
// docs/37: the top bar names the lobby (the keeper's label) with the storyteller's World name in the
// eyebrow; the options rename the lobby (`rename_lobby`) and carry no seed phrase.
// docs/62: a gathering lobby has no World yet (it is chosen at Begin, at the lobby's peril), so the
// eyebrow says so until the World is named.
// docs/58: YOUR HEROES became the Outfitting panel (the founding purse, recruits, trained stat cards
// and the shared founding market); the roster is no longer an option, the starting gold is each
// player's founding purse.
// docs/63: the keeper chooses the storyteller only if their account allows uncensored lobbies;
// everyone else sees it only when the World is uncensored.
// docs/132: my seat card's swatch chooses my player colour.
// docs/146: each hero in Your company has a gear row into the inventory (I and K work here too).
import { useEffect, useRef, useState } from 'react';
import { OFFLINE_TITLE, useLinkUp } from '../../core/stores/link';
import { usePlace } from '../../core/audio/places';
import { Badge, Button, Field, Input, Modal, Panel, Segmented, Select, Toggle, confirmDialog } from '../components';
import { useSession } from '../../core/stores/session';
import { useRoute } from '../../core/stores/route';
import { useAllowUncensored, useHeroCallings, useHeroOrigins, useLobbySeats, useMyWorldInvites, useProfileMap, useWorld, useWorldHeroes, useWorldMembers, useWorldSettings } from '../../core/subscriptions/hooks';
import { rpc, useToasts } from '../../core/net/reducers';
import { identityHex } from '../../core/net/connection';
import type { Hero, WorldOptions } from '../../bindings/types';
import { ChatDock, usePlayerColours } from './ChatDock';
import { ColourButton } from './ColourPicker';
import { FriendsPanel, PHASE_GATHERING } from './PlayOnline';
import { HeroForge } from './HeroForge';
import { CompanyPanel, MarketPanel, useFounding } from './Outfitting';
import { JoinPolicy, LobbyBottom, LobbyFrame, LobbyTop, OpenSeat, SeatCard, SeatRow, Who, heroStats, nameList, setVisibility, useHere, type SeatTone } from './Lobby';
import { LobbyNameField } from './LobbyName';
import { isNamed, worldTitle } from '../../core/story/worldName';
import { isHotkey } from '../../core/input/hotkeys';
import { openInventory } from '../inventory/store';
import { openHeroSheet } from '../heroes/heroSheetStore';
import { STORYTELLERS, STORYTELLER_STANDARD } from '../../core/story/storyteller';
import './gathering.css';

const WINDOWS = [0, 30, 60, 120, 300, 600];
const windowLabel = (s: number) => (s === 0 ? 'Unlimited' : s < 60 ? `${s} s` : `${s / 60} min`);
const PERIL = [{ value: 75, label: 'Gentle' }, { value: 100, label: 'Standard' }, { value: 125, label: 'Grim' }, { value: 150, label: 'Dire' }];
const XP = [50, 100, 150, 200];
const GOLD = [0, 25, 50, 100, 150, 200, 250, 300, 400, 500];
/** 1–20 seats (fk-world `MAX_SEATS`, docs/50 PC1). */
const SEATS = Array.from({ length: 20 }, (_, i) => i + 1);
/** How long a lost seat must stay lost before the lobby lets go of the player (the rows empty while subscriptions change). */
const SEAT_LOSS_MS = 2000;

/** The keeper's options: live controls (keeper) or the same controls disabled (everyone else), in
 *  the Lobby settings modal (opened from the top bar). */
function OptionsForm({ worldId, options: server, keeper, begun, seated, storyteller }: { worldId: bigint; options: WorldOptions; keeper: boolean; begun: boolean; seated: number; storyteller: number }) {
  const allowUncensored = useAllowUncensored();
  const [teller, setTeller] = useState<number | undefined>();
  useEffect(() => { setTeller(undefined); }, [storyteller]);
  const shownTeller = teller ?? storyteller;
  const chooseTeller = (v: number) => { setTeller(v); void rpc.setLobbyStoryteller(worldId, v).then((r) => { if (!r.ok) setTeller(undefined); }); };
  const [pending, setPending] = useState<Partial<WorldOptions>>({});
  useEffect(() => {
    setPending((p) => {
      const left = Object.fromEntries(Object.entries(p).filter(([k, v]) => server[k as keyof WorldOptions] !== v));
      return Object.keys(left).length === Object.keys(p).length ? p : left;
    });
  }, [server]);
  const options: WorldOptions = { ...server, ...pending };
  const [text, setText] = useState({ description: server.description });
  useEffect(() => { setText({ description: server.description }); }, [server.description]);
  const set = (patch: Partial<WorldOptions>) => {
    if (!keeper) return;
    const next = { ...pending, ...patch };
    setPending(next);
    void rpc.updateWorldSettings(worldId, { ...server, ...next }).then((r) => { if (!r.ok) setPending({}); });
  };
  const commitText = () => { if (text.description !== options.description) set(text); };
  const off = !keeper;
  const founding = off || begun;
  const onEnter = (e: React.KeyboardEvent<HTMLInputElement>) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); };
  return (
    <>
      {!keeper && <Badge tone="grey">set by the keeper</Badge>}
      <div className="fk-gather__opts" id="fk-gather-opts">
        <LobbyNameField worldId={worldId} name={server.name} keeper={keeper} testId="opt-name" id="fk-opt-name" className="fk-gather__opt--wide" />
        <Field label="Seats" htmlFor="fk-opt-seats"><Select id="fk-opt-seats" data-testid="opt-seats" value={options.maxPlayers} disabled={off} onChange={(e) => set({ maxPlayers: Number(e.target.value) })}>{SEATS.map((n) => <option key={n} value={n} disabled={n < seated}>{n} player{n === 1 ? '' : 's'}</option>)}</Select></Field>
        <Field label="Planning window" htmlFor="fk-opt-window"><Select id="fk-opt-window" data-testid="opt-window" value={options.planningWindowS} disabled={off} onChange={(e) => set({ planningWindowS: Number(e.target.value) })}>{WINDOWS.map((s) => <option key={s} value={s}>{windowLabel(s)}</option>)}</Select></Field>
        <Field label="Founding gold" htmlFor="fk-opt-gold" hint="each player's, spent while joining"><Select id="fk-opt-gold" data-testid="opt-gold" value={options.startingGold} disabled={founding} onChange={(e) => set({ startingGold: Number(e.target.value) })}>{GOLD.map((g) => <option key={g} value={g}>{g} gold</option>)}</Select></Field>
        <Field label="Peril"><div data-testid="opt-peril" data-value={options.perilPct}><Segmented label="Peril" value={options.perilPct} disabled={off} onChange={(v) => set({ perilPct: v })} options={PERIL.map((p) => ({ value: p.value, label: p.label, title: `${p.value}% of the encounter dial` }))} /></div></Field>
        {keeper && (allowUncensored || storyteller !== STORYTELLER_STANDARD)
          ? <Field label="Storyteller" hint={STORYTELLERS.find((t) => t.value === shownTeller)?.about || undefined}><div data-testid="opt-storyteller" data-value={shownTeller}><Segmented label="Storyteller" value={shownTeller} disabled={begun || !allowUncensored} onChange={chooseTeller} options={STORYTELLERS.map((t) => ({ value: t.value, label: t.label, title: t.about || 'Written as every World is' }))} /></div></Field>
          : storyteller !== STORYTELLER_STANDARD && <Field label="Storyteller" hint={`the keeper chose it: ${STORYTELLERS.find((t) => t.value === storyteller)?.about ?? 'uncensored'}`}><div data-testid="opt-storyteller" data-value={storyteller}><Badge tone="red">{STORYTELLERS.find((t) => t.value === storyteller)?.label ?? 'Uncensored'}</Badge></div></Field>}
        <Field label="XP rate"><div data-testid="opt-xp" data-value={options.xpRatePct}><Segmented label="XP rate" value={options.xpRatePct} disabled={off} onChange={(v) => set({ xpRatePct: v })} options={XP.map((x) => ({ value: x, label: `${x}%` }))} /></div></Field>
        <div className="fk-gather__opt--wide fk-gather__toggles">
          <Toggle label="Pausing allowed" checked={options.allowPausing} disabled={off} onChange={(e) => set({ allowPausing: e.target.checked })} data-testid="opt-pausing" />
          <Toggle label="Late joining" checked={options.lateJoin} disabled={off} onChange={(e) => set({ lateJoin: e.target.checked })} data-testid="opt-late" />
        </div>
        <Field label="Description" htmlFor="fk-opt-desc" className="fk-gather__opt--wide"><Input id="fk-opt-desc" data-testid="opt-description" value={text.description} placeholder={keeper ? 'a line for the lobby list' : '—'} disabled={off} maxLength={200} onChange={(e) => setText({ ...text, description: e.target.value })} onBlur={commitText} onKeyDown={onEnter} /></Field>
      </div>
      {begun && keeper && <p className="fk-faint fk-gather__note">The founding gold was fixed when the World began; the lobby name can always change.</p>}
    </>
  );
}

export function GatheringScreen() {
  const up = useLinkUp();
  usePlace({ key: 'place.tavern' });   // docs/44: the party gathers at the inn
  const worldId = useSession((s) => s.worldId);
  const setWorld = useSession((s) => s.setWorld);
  const me = useSession((s) => s.identity ?? '');
  const go = useRoute((s) => s.go);
  const world = useWorld(worldId);
  const settings = useWorldSettings(worldId);
  const members = useWorldMembers(worldId);
  const seats = useLobbySeats(worldId);
  const heroes = useWorldHeroes(worldId).filter((h) => h.status !== 2);
  const origins = useHeroOrigins(worldId);
  const callings = useHeroCallings(worldId);
  const callingOf = (heroId: bigint) => callings.find((k) => k.heroId === heroId);
  const invites = useMyWorldInvites().filter((i) => i.worldId === worldId && identityHex(i.from) === me);
  const profiles = useProfileMap();
  const colours = usePlayerColours(worldId);
  const here = useHere(worldId);
  const push = useToasts((s) => s.push);
  const [busy, setBusy] = useState<string | undefined>();
  const [forging, setForging] = useState(false);
  const [editing, setEditing] = useState<Hero | undefined>();
  const [inviting, setInviting] = useState(false);
  const [optionsOpen, setOptionsOpen] = useState(false);
  const founding = useFounding(worldId);
  // seated here: read from this lobby's own member rows, which stay subscribed while it is open. The
  // account view is re-subscribed as the screen changes, and its rows are briefly empty then.
  const seatedHere = members.some((m) => identityHex(m.owner) === me);
  // docs/146: I and K open my first hero's inventory and stats card here too (the tabs reach the rest)
  const firstMine = useWorldHeroes(worldId).filter((h) => h.status !== 2 && identityHex(h.owner) === me).sort((a, b) => (a.id < b.id ? -1 : 1))[0]?.id;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (firstMine === undefined || e.repeat) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return;
      if (document.querySelector('[aria-modal="true"]')) return;
      if (isHotkey(e, 'town.inventory')) { e.preventDefault(); openInventory(firstMine); }
      else if (isHotkey(e, 'town.heroSheet')) { e.preventDefault(); openHeroSheet(firstMine); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [firstMine]);

  // Shown the door: back to Play online. Only a seat lost while this World is still the one open
  // here counts, and only once it stays lost: changing subscriptions empties the member rows for a
  // moment. Leaving it (Back, Leave lobby) clears the World first, so it is not a kick.
  const [seatedIn, setSeatedIn] = useState<bigint>();
  const departing = useRef(false);
  useEffect(() => {
    if (seatedHere) { setSeatedIn(worldId); return; }
    if (seatedIn === undefined || seatedIn !== worldId || departing.current) return;
    const t = setTimeout(() => { push('You are no longer seated in that World.', 'info'); setWorld(undefined); go('world'); }, SEAT_LOSS_MS);
    return () => clearTimeout(t);
  }, [seatedHere, seatedIn, worldId, push, setWorld, go]);
  // An invitation sent from the friends list closes it: the invited card shows in the seat row.
  const [invitedCount, setInvitedCount] = useState(invites.length);
  useEffect(() => { if (invites.length > invitedCount) setInviting(false); if (invites.length !== invitedCount) setInvitedCount(invites.length); }, [invites.length, invitedCount]);

  if (!worldId || !world || !settings) {
    return <main className="fk-screen fk-screen--gathering" data-testid="gathering-screen"><div className="fk-center"><Panel className="fk-hit" title="The lobby"><p className="fk-muted">Reaching the lobby…</p></Panel></div></main>;
  }
  const options = settings.options;
  const lobbyName = options.name || worldTitle(world);
  const keeperHex = identityHex(world.owner);
  const keeper = keeperHex === me;
  const begun = settings.phase !== PHASE_GATHERING;
  const mine = heroes.filter((h) => identityHex(h.owner) === me).sort((a, b) => (a.id < b.id ? -1 : 1));
  const { recruit } = founding;
  const seatOf = (hex: string) => seats.find((s) => identityHex(s.owner) === hex);
  const sortedMembers = [...members].sort((a, b) => (identityHex(a.owner) === keeperHex ? -1 : identityHex(b.owner) === keeperHex ? 1 : a.id < b.id ? -1 : 1));
  const heroesOf = (hex: string) => heroes.filter((h) => identityHex(h.owner) === hex).sort((a, b) => (a.id < b.id ? -1 : 1));
  const waitingOn = sortedMembers.filter((m) => identityHex(m.owner) !== keeperHex && !seatOf(identityHex(m.owner))?.ready);
  const heroless = sortedMembers.filter((m) => heroesOf(identityHex(m.owner)).length === 0);
  const myReady = !!seatOf(me)?.ready;
  const originOf = (heroId: bigint) => origins.find((o) => o.heroId === heroId);
  // A seat is ready when it has a hero and (a guest) has said so; the keeper's Begin is their ready.
  const seatTone = (hex: string): SeatTone => (heroesOf(hex).length === 0 ? 'choosing' : hex === keeperHex || seatOf(hex)?.ready ? 'ready' : 'idle');
  const TONE_LABEL: Record<SeatTone, string> = { ready: 'Ready', choosing: 'Choosing hero', idle: 'Not ready' };

  const run = async (key: string, fn: () => Promise<unknown>) => { setBusy(key); await fn(); setBusy(undefined); };
  const back = () => { setWorld(undefined); go('world'); };
  const leave = async () => {
    if (keeper) { back(); return; }
    const ok = await confirmDialog({ title: `Leave ${lobbyName}?`, text: 'Your seat and the heroes you made here go with you. You can be invited back.', confirmLabel: 'Leave', danger: true });
    if (!ok) return;
    departing.current = true;
    const r = await rpc.leaveWorldLobby(worldId);
    if (r.ok) back(); else departing.current = false;
  };
  const kick = async (hex: string, name: string) => {
    const ok = await confirmDialog({ title: `Show ${name} the door?`, text: 'Their seat and the heroes they made here are removed.', confirmLabel: 'Remove', danger: true });
    if (ok) await rpc.kickFromWorld(worldId, hex);
  };

  const emptySeats = Math.max(0, options.maxPlayers - members.length);
  const blockers = [
    ...heroless.map((m) => `${m.displayName} has no hero yet`),
    ...waitingOn.filter((m) => !heroless.includes(m)).map((m) => `${m.displayName} is not ready`),
  ];
  const readyCount = sortedMembers.filter((m) => seatTone(identityHex(m.owner)) === 'ready').length;
  const notReady = sortedMembers.filter((m) => seatTone(identityHex(m.owner)) !== 'ready').map((m) => ({ key: m.id.toString(), name: identityHex(m.owner) === me ? 'you' : m.displayName, colour: colours.get(identityHex(m.owner)) }));
  const policyLine = ['Anyone else needs an invitation.', 'Friends of the keeper may join; others need an invitation.', 'Anyone may join from the lobby list.'][options.visibility] ?? '';

  const top = (
    <LobbyTop back="Play online" onBack={back} backTestId="gather-back" eyebrow={`World lobby${isNamed(world) ? ` · ${worldTitle(world)}` : begun ? ' · the storyteller is writing' : ''}${begun ? ' · begun' : ''}`} name={lobbyName} nameTestId="gather-name"
      right={<>
        <JoinPolicy value={options.visibility} keeper={keeper} onChange={(v) => setVisibility(worldId, options, v)} />
        <Button small onClick={() => setOptionsOpen(true)} data-testid="world-options-toggle">Settings</Button>
      </>} />
  );

  const seatRow = (
    <SeatRow cards={sortedMembers.length + invites.length + (emptySeats > 0 && !begun ? 1 : 0)} data-testid="seats" aria-label="Seats">
      {sortedMembers.map((m) => {
        const hex = identityHex(m.owner);
        const seat = seatOf(hex);
        const isKeeper = hex === keeperHex;
        const theirs = heroesOf(hex);
        const p = profiles.get(hex);
        const tone = seatTone(hex);
        return (
          <SeatCard key={m.id.toString()} colour={colours.get(hex)} player={m.displayName} crown={isKeeper} you={hex === me} heroes={theirs} callings={callings} heroTestId="seat-hero"
            stats={theirs.length === 1 ? heroStats(theirs[0]!, originOf(theirs[0]!.id), callingOf(theirs[0]!.id)) : undefined} tone={tone} status={TONE_LABEL[tone]}
            title={`${p ? `@${p.username}` : 'no username'}${p ? (p.online ? ' · online' : ' · away') : ''}`}
            tools={<>
              {p && <span className={`fk-lobby__dot ${p.online ? 'fk-lobby__dot--on' : ''}`} aria-label={p.online ? 'online' : 'away'} />}
              {hex === me && <ColourButton worldId={worldId} className="fk-colour-btn--seat" />}
              {keeper && !isKeeper && !begun && <button type="button" className="fk-lobby__x" title="Show them the door" aria-label={`Show ${m.displayName} the door`} onClick={() => void kick(hex, m.displayName)} data-testid="seat-kick">✕</button>}
            </>}
            data-testid="seat" data-name={m.displayName} data-username={p?.username} data-ready={!!seat?.ready} data-keeper={isKeeper} />
        );
      })}
      {invites.map((i) => {
        const p = profiles.get(identityHex(i.to));
        return (
          <OpenSeat key={i.id.toString()} title="Invited" data-testid="invited" className="fk-lobby__seat fk-lobby__seat--open fk-lobby__seat--invited"
            text={<><b className="fk-lobby__invitee">{p ? `@${p.username}` : identityHex(i.to).slice(-6)}</b><br />has an invitation waiting on their Play online screen.</>}
            action={<Button small onClick={() => void rpc.respondWorldInvite(i.id, false)} data-testid="invite-withdraw">Withdraw</Button>} />
        );
      })}
      {emptySeats > 0 && !begun && (
        <OpenSeat data-testid="seat-open" title={emptySeats === 1 ? 'Open seat' : `${emptySeats} open seats`}
          text={`This World seats ${options.maxPlayers}. ${policyLine}`}
          action={<Button small onClick={() => setInviting(true)} data-testid="seat-invite">Invite a friend</Button>} />
      )}
    </SeatRow>
  );

  const strip = <CompanyPanel worldId={worldId} onRecruit={() => setForging(true)} onEdit={setEditing} />;

  const allReady = blockers.length === 0;
  const summary = begun ? 'The World has begun.'
    : <><b>{readyCount} of {members.length} ready.</b> {notReady.length ? <>Waiting on {nameList(notReady)}.</> : keeper ? 'Begin when you are.' : myReady ? <>Waiting for <Who name={sortedMembers[0]?.displayName ?? 'the keeper'} colour={colours.get(keeperHex)} /> to begin.</> : 'Ready up when your heroes are made.'}</>;

  const bottom = (
    <LobbyBottom squares={sortedMembers.map((m) => ({ key: m.id.toString(), colour: colours.get(identityHex(m.owner)), on: seatTone(identityHex(m.owner)) === 'ready', title: m.displayName }))}
      summary={summary} summaryTestId={keeper ? 'lobby-blockers' : 'lobby-summary'}
      actions={<>
        <Button size="lg" onClick={() => void leave()} data-testid="gather-leave">{keeper ? 'Back' : 'Leave lobby'}</Button>
        {keeper
          ? <Button primary size="lg" disabled={!up || !!busy || !allReady || begun} loading={busy === 'begin'} onClick={() => void run('begin', () => rpc.beginWorld(worldId))} data-testid="lobby-begin" title={!up ? OFFLINE_TITLE : blockers.join(' · ') || 'The quest board is posted the moment you begin'}>Begin the World</Button>
          : <Button primary={!myReady} size="lg" disabled={!up || !!busy || mine.length === 0} loading={busy === 'ready'} onClick={() => void run('ready', () => rpc.setLobbyReady(worldId, !myReady))} data-testid="lobby-ready" aria-pressed={myReady}
              title={mine.length === 0 ? 'Forge a hero before you ready up' : myReady ? 'Press again to stand down. A change to the options un-readies everyone.' : undefined}>{myReady ? 'Ready ✓' : 'Ready'}</Button>}
      </>} />
  );

  return (
    <LobbyFrame screen="gathering" testId="gathering-screen" data-phase={settings.phase} top={top}
      main={<>{seatRow}<div className="fk-lobby__lower fk-gather__lower">{strip}<MarketPanel worldId={worldId} /></div></>}
      side={<ChatDock worldId={worldId} variant="panel" className="fk-lobby__chat" title="Party chat" here={here} />}
      bottom={<>
        {bottom}
        <Modal open={forging} onClose={() => setForging(false)} eyebrow={isNamed(world) ? worldTitle(world) : lobbyName} title="Forge a hero" size="full" testId="forge-modal">
          <HeroForge worldId={worldId} rosterFull={!recruit.ok} fee={recruit.fee} capNote={recruit.why} onForged={() => setForging(false)} />
        </Modal>
        <Modal open={!!editing} onClose={() => setEditing(undefined)} eyebrow={isNamed(world) ? worldTitle(world) : lobbyName} title={`Edit ${editing?.name ?? 'hero'}`} size="full" testId="forge-edit-modal">
          {editing && <HeroForge key={editing.id.toString()} worldId={worldId} hero={editing} onForged={() => setEditing(undefined)} />}
        </Modal>
        <Modal open={optionsOpen} onClose={() => setOptionsOpen(false)} eyebrow={lobbyName} title="Lobby settings" size="wide" testId="world-options"
          footer={<Button primary onClick={() => setOptionsOpen(false)} data-testid="world-options-done">Done</Button>}>
          <OptionsForm worldId={worldId} options={options} keeper={keeper} begun={begun} seated={members.length} storyteller={world.storyteller} />
        </Modal>
        <Modal open={inviting} onClose={() => setInviting(false)} eyebrow={lobbyName} title="Invite a friend" size="narrow" testId="invite-modal">
          <FriendsPanel inviteWorldId={worldId} className="fk-lobby__friends" />
        </Modal>
      </>} />
  );
}
